// src/components/dashboard/InsightsDashboard.jsx
/**
 * @file components/dashboard/InsightsDashboard.jsx
 * @description Per-user routing/savings dashboard.
 *
 * Hits GET /v1/insights with the user's aridel_live_… key and renders three cards:
 *   1. Total saved $ (vs always-routing-to-Opus baseline)
 *   2. Model distribution donut (which models the router picked, by request count)
 *   3. Model latency bars (avg ms per chosen model)
 *
 * No chart library — pure CSS/SVG to keep the bundle slim. The donut is a
 * conic-gradient; the bars are flex-grow divs sized by the latency spread.
 *
 * Per the implementation plan, this is the headline customer-facing surface.
 * The "$142 saved this month" number is the one that closes deals.
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
    ArrowLeft, BarChart3, DollarSign, Zap, AlertCircle,
    RefreshCw, Loader, ExternalLink, Award, TrendingUp, TrendingDown,
    Tag,
} from 'lucide-react';
import { useChatSession } from '../../context/ChatSessionContext';
import { insights as fetchInsights } from '../../aridel-client';
import settingsStyles from '../../styles/Settings.module.css';
import styles from './Dashboard.module.css';

// 6-color palette for the donut. Loops if more than 6 models appear.
const PALETTE = [
    '#6366f1', // indigo-500
    '#10b981', // emerald-500
    '#f59e0b', // amber-500
    '#ef4444', // red-500
    '#06b6d4', // cyan-500
    '#a855f7', // purple-500
];

const fmtUsd = (n) =>
    typeof n === 'number'
        ? `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`
        : '—';

const fmtPct = (n) =>
    typeof n === 'number' ? `${n.toFixed(1)}%` : '—';


/** Donut chart from a conic-gradient — no external library. */
const ModelDonut = ({ slices }) => {
    if (!slices?.length) return null;
    let acc = 0;
    const stops = slices.map((s, i) => {
        const start = acc;
        acc += s.percentage;
        return `${PALETTE[i % PALETTE.length]} ${start}% ${acc}%`;
    });
    const gradient = `conic-gradient(${stops.join(', ')})`;
    return (
        <div className={styles.donutWrap}>
            <div className={styles.donut} style={{ background: gradient }}>
                <div className={styles.donutHole} />
            </div>
            <ul className={styles.donutLegend}>
                {slices.map((s, i) => (
                    <li key={s.model}>
                        <span className={styles.swatch}
                            style={{ background: PALETTE[i % PALETTE.length] }} />
                        <span className={styles.legendModel}>{s.model}</span>
                        <span className={styles.legendPct}>{fmtPct(s.percentage)}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};


/** Horizontal bar chart, max-bar = 100% width. */
const LatencyBars = ({ rows }) => {
    if (!rows?.length) return null;
    const max = Math.max(...rows.map(r => r.avg_latency_ms || 0)) || 1;
    return (
        <ul className={styles.bars}>
            {rows.map((r, i) => {
                const widthPct = (r.avg_latency_ms || 0) * 100 / max;
                return (
                    <li key={r.model} className={styles.barRow}>
                        <span className={styles.barLabel}>{r.model}</span>
                        <div className={styles.barTrack}>
                            <div className={styles.barFill}
                                style={{
                                    width: `${widthPct}%`,
                                    background: PALETTE[i % PALETTE.length],
                                }} />
                        </div>
                        <span className={styles.barValue}>
                            {Math.round(r.avg_latency_ms || 0)} ms
                        </span>
                    </li>
                );
            })}
        </ul>
    );
};


/** Top question categories — what topics your traffic clusters into, plus
    a "best model for this" line (from the routing centroid file's winner per
    benchmark). Shows what the customer's app DOES + what they should consider
    using for it.

    Falls back to a one-line "no data yet" hint for users who haven't
    triggered any auto-routed traffic (we only count categories on
    `model:"auto"` requests, where the worker classifies the prompt). */
const TopCategoriesList = ({ rows }) => {
    if (!rows?.length) {
        return (
            <p className={styles.muted}>
                No category data yet. Send a few <code>model:"auto"</code>{' '}
                requests — we'll classify each prompt and surface what your
                app asks about most.
            </p>
        );
    }
    const max = Math.max(...rows.map(r => r.request_count || 0)) || 1;
    const color = i => ['#6366f1', '#10b981', '#f59e0b'][i % 3];
    // Render a model name + an inline "upgrade to access" hint when the
    // centroid file's recommendation isn't reachable through this catalog.
    const ModelName = ({ name, available }) => {
        if (!name) return null;
        if (available === false) {
            return (
                <>
                    <span className={styles.bestModelName}>{name}</span>
                    <span className={styles.upgradeBadge} title="Not in your catalog — upgrade to access">
                        upgrade to access
                    </span>
                </>
            );
        }
        return <span className={styles.bestModelName}>{name}</span>;
    };
    return (
        <ul className={styles.categoriesList}>
            {rows.map((r, i) => {
                const widthPct = (r.request_count || 0) * 100 / max;
                const rec = r.recommended || {};
                const avail = rec.availability || {};
                return (
                    <li key={r.benchmark} className={styles.categoryRow}>
                        <div className={styles.barRow}>
                            <span className={styles.barLabel} title={r.benchmark}>
                                {r.category}
                            </span>
                            <div className={styles.barTrack}>
                                <div className={styles.barFill}
                                    style={{ width: `${widthPct}%`, background: color(i) }} />
                            </div>
                            <span className={styles.barValue}>{r.percentage.toFixed(1)}%</span>
                        </div>
                        {(rec.best_model || rec.best_open_model) && (
                            <div className={styles.bestModelLine}>
                                {rec.best_model && (
                                    <span>
                                        <strong>Best for this:</strong>{' '}
                                        <ModelName name={rec.best_model} available={avail.best_model} />
                                    </span>
                                )}
                                {rec.best_open_model && rec.best_open_model !== rec.best_model && (
                                    <span className={styles.bestOpenModel}>
                                        · open-source:{' '}
                                        <ModelName name={rec.best_open_model} available={avail.best_open_model} />
                                    </span>
                                )}
                            </div>
                        )}
                    </li>
                );
            })}
        </ul>
    );
};


/** Tier-fair performance gauge — your quality vs the average model the router
 *  would have considered at each prompt's complexity tier (not the catalog-wide
 *  average that includes Opus, which would unfairly penalize cheap-but-correct
 *  routes to Nova Lite / Haiku for easy prompts). */
const PerformanceGauge = ({ perf }) => {
    const { performance_delta_pct, by_complexity } = perf;
    const positive = (performance_delta_pct || 0) >= 0;

    return (
        <div className={styles.perfWrap}>
            <div className={`${styles.perfDelta} ${positive ? styles.deltaUp : styles.deltaDown}`}>
                {positive ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                <span>{positive ? '+' : ''}{(performance_delta_pct || 0).toFixed(1)}%</span>
                <span className={styles.deltaText}>
                    vs same-complexity average
                </span>
            </div>

            {/* Per-complexity breakdown (small print) */}
            {by_complexity && (by_complexity.low || by_complexity.high) && (
                <div className={styles.perfBreakdown}>
                    {by_complexity.low && (
                        <div className={styles.perfBreakdownRow}>
                            <span className={styles.perfBreakdownLabel}>
                                LOW complexity ({by_complexity.low.request_count} req)
                            </span>
                            <span className={styles.perfBreakdownValue}>
                                <em>({by_complexity.low.performance_delta_pct >= 0 ? '+' : ''}
                                    {by_complexity.low.performance_delta_pct}%)</em>
                            </span>
                        </div>
                    )}
                    {by_complexity.high && (
                        <div className={styles.perfBreakdownRow}>
                            <span className={styles.perfBreakdownLabel}>
                                HIGH complexity ({by_complexity.high.request_count} req)
                            </span>
                            <span className={styles.perfBreakdownValue}>
                                <em>({by_complexity.high.performance_delta_pct >= 0 ? '+' : ''}
                                    {by_complexity.high.performance_delta_pct}%)</em>
                            </span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};


/** Period tabs — day/week/month/year/overall. `overall` is the lifetime row
 *  and includes pre-v18 traffic (when per-day buckets didn't exist yet). The
 *  others aggregate `UserInsightsDaily-<env>` over the last N day-rows. */
const PERIOD_OPTIONS = [
    { key: 'day',     label: 'Today',    headline: 'today' },
    { key: 'week',    label: 'Week',     headline: 'this week' },
    { key: 'month',   label: 'Month',    headline: 'this month' },
    { key: 'year',    label: 'Year',     headline: 'this year' },
    { key: 'overall', label: 'Overall',  headline: 'overall' },
];

const PeriodTabs = ({ value, onChange, disabled }) => (
    <div className={styles.periodTabs} role="tablist" aria-label="Insights period">
        {PERIOD_OPTIONS.map((opt) => (
            <button
                key={opt.key}
                role="tab"
                aria-selected={value === opt.key}
                disabled={disabled}
                className={`${styles.periodTab} ${value === opt.key ? styles.periodTabActive : ''}`}
                onClick={() => onChange(opt.key)}
            >
                {opt.label}
            </button>
        ))}
    </div>
);


const InsightsDashboard = () => {
    const { setViewMode } = useChatSession();
    const [period, setPeriod] = useState('month');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async (nextPeriod) => {
        const p = nextPeriod || period;
        setLoading(true);
        setError(null);
        try {
            const insights = await fetchInsights({ period: p });
            setData(insights);
        } catch (e) {
            setError(e);
        } finally {
            setLoading(false);
        }
    }, [period]);

    useEffect(() => {
        load();
    }, [load]);

    const handlePeriodChange = (nextPeriod) => {
        if (nextPeriod === period) return;
        setPeriod(nextPeriod);
        load(nextPeriod);
    };

    const periodMeta = PERIOD_OPTIONS.find(o => o.key === period) || PERIOD_OPTIONS[2];

    const handleBack = () => setViewMode('chat');

    if (loading && !data) {
        return (
            <div className={settingsStyles.settingsContainer}>
                <div className={settingsStyles.settingsContent}>
                    <div className={styles.spinnerWrap}>
                        <Loader className={styles.spinner} size={32} />
                        Loading insights…
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={settingsStyles.settingsContainer}>
                <div className={settingsStyles.settingsHeader}>
                    <button onClick={handleBack} className={settingsStyles.backButton}>
                        <ArrowLeft size={20} /> Back to Chat
                    </button>
                </div>
                <div className={settingsStyles.settingsContent}>
                    <div className={`${settingsStyles.alertBox} ${settingsStyles.warning}`}>
                        <AlertCircle size={20} />
                        <div className={settingsStyles.alertContent}>
                            <p className={settingsStyles.alertTitle}>Couldn't load insights</p>
                            <p className={settingsStyles.alertMessage}>{error.message}</p>
                        </div>
                    </div>
                    <button className={styles.primaryBtn} onClick={load}>
                        <RefreshCw size={16} /> Retry
                    </button>
                </div>
            </div>
        );
    }

    // We have data. Pull the headline numbers.
    const totalRequests = data?.total_requests || 0;
    const totalSpent = data?.total_spent_usd || 0;
    const vsPremium = data?.savings?.vs_premium_baseline || {};
    const vsMaxAvail = data?.savings?.vs_most_expensive_available;
    const vsAvgAvail = data?.savings?.vs_average_available;
    // v15: vs_most_used_model removed in favor of top_question_categories.
    // v13: vs_openrouter_default removed — was a misnamed local proxy.
    const distribution = data?.model_distribution || [];
    const performance = data?.performance;
    const topCategories = data?.top_question_categories || [];
    const complexitySplit = data?.complexity_split;

    return (
        <div className={settingsStyles.settingsContainer}>
            {/* Header */}
            <div className={settingsStyles.settingsHeader}>
                <button onClick={handleBack} className={settingsStyles.backButton}>
                    <ArrowLeft size={20} /> Back to Chat
                </button>
                <h1 className={settingsStyles.settingsTitle}>Routing Insights</h1>
                <p className={settingsStyles.settingsSubtitle}>
                    {totalRequests.toLocaleString()} requests · {fmtUsd(totalSpent)} spent
                    {' '}<span className={styles.muted}>· {periodMeta.headline}</span>
                </p>
                <PeriodTabs value={period} onChange={handlePeriodChange} disabled={loading} />
                <button className={styles.refreshBtn} onClick={() => load()} disabled={loading}>
                    <RefreshCw size={16} className={loading ? styles.spinner : ''} />
                    Refresh
                </button>
            </div>

            <div className={settingsStyles.settingsContent}>
                {totalRequests === 0 && (
                    <div className={`${settingsStyles.alertBox}`}>
                        <AlertCircle size={20} />
                        <div className={settingsStyles.alertContent}>
                            <p className={settingsStyles.alertTitle}>No requests yet</p>
                            <p className={settingsStyles.alertMessage}>
                                Make some <code>model: "auto"</code> calls to the public API and
                                this dashboard will populate.
                            </p>
                        </div>
                    </div>
                )}

                {/* THE THREE CARDS */}
                <div className={styles.cardGrid}>

                    {/* Card 1: Headline saved $ */}
                    <div className={styles.headlineCard}>
                        <div className={styles.headlineLabel}>
                            <DollarSign size={16} /> Saved {periodMeta.headline}
                        </div>
                        <div className={styles.headlineNumber}>
                            {fmtUsd(vsPremium.saved_usd)}
                        </div>
                        <div className={styles.headlineSub}>
                            {fmtPct(vsPremium.percentage)} less than always routing to{' '}
                            <strong>{vsPremium.baseline_model || 'premium'}</strong>
                        </div>
                        {/* Secondary baselines, smaller print */}
                        <ul className={styles.baselineList}>
                            {vsMaxAvail && (
                                <li>
                                    <span>vs most expensive available ({vsMaxAvail.baseline_model})</span>
                                    <strong>{fmtUsd(vsMaxAvail.saved_usd)}</strong>
                                </li>
                            )}
                            {vsAvgAvail && (
                                <li>
                                    <span>vs average-priced model in your catalog</span>
                                    <strong>{fmtUsd(vsAvgAvail.saved_usd)}</strong>
                                </li>
                            )}
                        </ul>
                    </div>

                    {/* Card 2: Model donut */}
                    <div className={styles.card}>
                        <div className={styles.cardLabel}>
                            <BarChart3 size={16} /> Model distribution
                        </div>
                        {distribution.length ? (
                            <ModelDonut slices={distribution} />
                        ) : (
                            <p className={styles.muted}>No data yet</p>
                        )}
                    </div>

                    {/* Card 3: Latency bars */}
                    <div className={styles.card}>
                        <div className={styles.cardLabel}>
                            <Zap size={16} /> Avg latency (ms) per model
                        </div>
                        {distribution.length ? (
                            <LatencyBars rows={distribution} />
                        ) : (
                            <p className={styles.muted}>No data yet</p>
                        )}
                    </div>

                    {/* Card 4: Performance vs catalog avg */}
                    {performance && (
                        <div className={styles.card}>
                            <div className={styles.cardLabel}>
                                <Award size={16} /> Quality vs catalog average
                            </div>
                            <PerformanceGauge perf={performance} />
                        </div>
                    )}

                    {/* Card 5: Top 3 question categories — what the customer's
                        app actually asks about, derived from routing benchmarks */}
                    <div className={styles.card}>
                        <div className={styles.cardLabel}>
                            <Tag size={16} /> Top question categories
                        </div>
                        {complexitySplit && complexitySplit.classified_total > 0 && (
                            <div className={styles.complexitySplit}>
                                <span className={styles.complexityPill} style={{ background: 'rgba(16, 185, 129, 0.18)', color: '#34d399' }}>
                                    {complexitySplit.low_percentage}% easy
                                </span>
                                <span className={styles.complexityPill} style={{ background: 'rgba(168, 85, 247, 0.18)', color: '#c084fc' }}>
                                    {complexitySplit.high_percentage}% complex
                                </span>
                                <span className={styles.complexityCount}>
                                    ({complexitySplit.classified_total} classified)
                                </span>
                            </div>
                        )}
                        <TopCategoriesList rows={topCategories} />
                    </div>
                </div>

                {/* Counterfactual section (only renders if there's something to show) */}
                {data?.counterfactual?.unlock_recommendations?.length > 0 && (
                    <div className={settingsStyles.settingsSection}>
                        <h2 className={settingsStyles.sectionTitle}>
                            <ExternalLink size={20} /> What you're missing
                        </h2>
                        <ul className={styles.unlockList}>
                            {data.counterfactual.unlock_recommendations.map((rec, i) => (
                                <li key={i} className={styles.unlockRow}>
                                    <span className={styles.unlockReason}>
                                        {rec.action || rec.blocked_by}
                                    </span>
                                    <span className={styles.unlockMeta}>
                                        {(rec.unlocks || []).join(', ')}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InsightsDashboard;
